import * as React from 'react';

import SuperCritical from 'lib/supercritical';
import { tzmoment } from 'lib/tzmoment';
import { noDefault } from 'lib/eventWrappers';

import { EnhancedEstimateQueryStatus, UnactionableReason } from 'server/enums/EnhancedEstimateQuery';

import {
    WartsilaEnrichmentsEstimateDetails,
    EnhancedEstimateQueryViewModel,
    EnhancedEstimateViewModel,
    EstimateRefreshViewModel
} from './estimateDetails';

import { TimeAgoTooltip } from 'portcall/pctooltip/view';

/**
 * Estimate details as a stand-alone card.
 */
export const EstimateDetailsCardView = SuperCritical(({ model: m } : { model : WartsilaEnrichmentsEstimateDetails }) =>
    <div className="wartsila-enrichments estimate-details estimate-details-card pc-card">
        <div className="estimate-details-header pc-card-header">
            <span className="title pc-card-title">Estimated Arrival</span>
            {m.EstimateRefresher() && !m.InitialRefreshPendingOrInProgress() && <RefreshLink refresher={m.EstimateRefresher()!} />}
        </div>
        {m.EstimateRefresher() && <FailedRefreshView refresher={m.EstimateRefresher()!} />}
        <EstimateDetailsBody model={m} />
    </div>
);

/**
 * Estimate details in a condensed view for embedding in other cards.
 */
export const EstimateDetailsCondensedView = SuperCritical(({ model: m } : { model : WartsilaEnrichmentsEstimateDetails }) =>
    <div className="wartsila-enrichments estimate-details estimate-details-condensed">
        <div className="condensed-header">
            <span className="title">Last Estimated Arrival</span>
            {m.EstimateRefresher() && !m.InitialRefreshPendingOrInProgress() && <RefreshLink refresher={m.EstimateRefresher()!} />}
        </div>
        {m.EstimateRefresher() && <FailedRefreshView refresher={m.EstimateRefresher()!} />}
        <EstimateDetailsBody model={m} />
    </div>
);

let RefreshLink = SuperCritical(({ refresher: r } : { refresher : EstimateRefreshViewModel }) =>
    <a className="refresh-link" href="" onClick={noDefault(r.RefreshEstimate)}>Refresh</a>
);

let FailedRefreshView = SuperCritical(({ refresher: r } : { refresher : EstimateRefreshViewModel }) => {
    if (r.FailedRefreshAttempt()) {
        const { query, dismiss } = r.FailedRefreshAttempt()!;
        return (
            <div className="refresh-errors alert alert-danger">
                <UnsuccessfulQueryView query={query} />
                <div className="dismiss-container">
                    <a className="dismiss" href="" onClick={noDefault(dismiss)}>Dismiss</a>
                </div>
            </div>
        );
    } else {
        return null;
    }
});

let EstimateDetailsBody = SuperCritical(({ model: m } : { model : WartsilaEnrichmentsEstimateDetails }) =>
    <div className="estimate-details-body">
        {m.RelevantQuery() ?
            <EnhancedEstimateQueryView query={m.RelevantQuery()!} /> :
            <MissingQueryView />
        }
    </div>
);

let EnhancedEstimateQueryView = SuperCritical(({ query } : { query : EnhancedEstimateQueryViewModel }) =>
    query.Result() ? <EnhancedEstimateView estimate={query.Result()!} query={query} /> :
    query.Model.Status() !== EnhancedEstimateQueryStatus.Succeeded ? <UnsuccessfulQueryView query={query} /> :
    null
);

let EnhancedEstimateView = SuperCritical(({ estimate: est, query } : { estimate : EnhancedEstimateViewModel, query : EnhancedEstimateQueryViewModel }) =>
    <div className="enhanced-estimate">
        <EnhancedEstimateMain estimate={est} query={query} />
        <EnhancedEstimateMeta estimate={est} query={query} />
    </div>
);

let EnhancedEstimateMain = SuperCritical(({ estimate: est, query } : { estimate : EnhancedEstimateViewModel, query : EnhancedEstimateQueryViewModel }) =>
    <div className="enhanced-estimate-main">
        Based on its last reported position,
        {' '}
        <span className="enhanced-estimate-vessel">{query.Model.Vessel() && query.Model.Vessel()!.Name()}</span>
        {' '}
        could arrive by
        {' '}
        {est.Model.EstimatedArrival() && tzmoment(est.Model.EstimatedArrival()!).format('MM/DD/YYYY [at] HH:mm')}.
    </div>
);

let EnhancedEstimateMeta = SuperCritical(({ estimate: est, query } : { estimate : EnhancedEstimateViewModel, query : EnhancedEstimateQueryViewModel }) =>
    <div className="enhanced-estimate-meta">
        <span className="automation-name pc-text-smalllabel">by {query.Model.Automation() && query.Model.Automation()!.Name()}</span>
        {est.Timestamp() &&
            <TimeAgoTooltip
                className="estimate-time pc-text-smalllabel"
                date={() => est.Timestamp()!}
                now={est.Clock.now}
            />
        }
    </div>
);

let UnsuccessfulQueryView = SuperCritical(({ query } : { query : EnhancedEstimateQueryViewModel }) =>
    query.Model.Status() === EnhancedEstimateQueryStatus.Unactionable ?
        <UnactionableQueryView query={query} /> :
    query.Model.Status() === EnhancedEstimateQueryStatus.Failed ?
        <FailedQueryView query={query} /> :
    query.Model.Status() === EnhancedEstimateQueryStatus.Querying ?
        <InProgressQueryView /> :
    null
);

let UnactionableQueryView = SuperCritical(({ query } : { query : EnhancedEstimateQueryViewModel }) => {
    const errors = [] as string[];

    // Missing IMO -- Expected and user-correctable.
    if ((query.Model.UnactionableReason() & UnactionableReason.NoValidVesselIdentifiers) !== 0) {
        errors.push("Missing required field IMO. Make sure your vessel has a valid IMO.");
    }

    // Missing destination zone or automation system config -- Unexpected so long as we've
    // configured the database correctly. Correctable via direct db access only.
    if ((query.Model.UnactionableReason() & UnactionableReason.NoValidDestinationZone) !== 0) {
        errors.push("Missing destination zone coordinates. Ask PortCall support to configure zone coordinates for the port arrival location.");
    }
    if ((query.Model.UnactionableReason() & UnactionableReason.MissingEnhancedEstimatesAutomationConfiguration) !== 0) {
        errors.push("Missing enhanced estimates automation configuration. Ask PortCall support to configure the enhanced estimates automation.");
    }

    return (
        <div className="enhanced-estimate-query-unactionable">
            <div>
                No enhanced estimate is available because of the following error{errors.length > 1 ? "s" : ""}:
            </div>
            <ul>
                {errors.map((err, idx) =>
                    <li key={idx}>{err}</li>
                )}
            </ul>
        </div>
    );
});

let FailedQueryView = SuperCritical(({ query } : { query : EnhancedEstimateQueryViewModel }) =>
    <div className="enhanced-estimate-query-failed">
        <div>
            Requesting an enhanced estimate failed with the following error:
        </div>
        <textarea
            className="error-details"
            disabled
            value={query.Model.FailureDetails() || ''}
        />
    </div>
);

let InProgressQueryView = () =>
    <div className="enhanced-estimate-query-inprogress">
        A request for an enhanced estimate is in progress. Please check back in a few minutes.
    </div>;

let MissingQueryView = () =>
    <div className="enhanced-estimate-query-missing">
        No enhanced estimate is available at this time.
    </div>



// WEBPACK FOOTER //
// ./apps/wartsila/wartsilaenrichments/estimateDetailsView.tsx